import axios from 'axios';
import { createRestApi, DefaultGetAllResponse } from '~/App/Api/rest';
import {
  CreditCard,
  Subscription,
  InvoiceInformation,
  PaymentIntent,
  InvoiceProfile,
  Country,
  CheckoutPlan,
} from '~/types/models';
import {
  AddOn,
  AddOnCheckoutDetails,
  AddOnPrice,
  AddOnType,
} from '~/types/models/addon';
import { useQuery, UseQueryOptions } from 'react-query';
import { FinalTypes } from '~/utils/types';

const { team } = window.Bonjoro;

export const creditCardsKeys = {
  all: ['credit-cards'] as const,
  lists: () => [...creditCardsKeys.all, 'list'] as const,
  details: () => [...creditCardsKeys.all, 'detail'] as const,
  detail: (id: string) => [...creditCardsKeys.details(), id] as const,
};

export type CreditCardsKeys = FinalTypes<typeof creditCardsKeys>;

export const invoiceApi = () =>
  createRestApi<InvoiceProfile>('/api/v3/billing/invoice/profile');
export const subscriptionApi = () =>
  createRestApi<Subscription[]>('/api/v3/billing/subscriptions');
export const countriesApi = () => createRestApi<Country[]>('/api/v3/countries');
export const creditCardsApi = () =>
  createRestApi<CreditCard>('/api/v3/billing/credit-cards');
export const invoiceInfoApi = () =>
  createRestApi<InvoiceInformation>('/api/v3/billing/invoice-info');

export interface CancelSubscriptionPostData {
  name: string;
  type: string;
  reason: string;
  messages: string;
  planSlug: string;
}

export interface CheckoutPostResponse {
  status: string;
}

export const cancelSubscription = async (data: CancelSubscriptionPostData) => {
  const result = await axios.post<Subscription>(
    `/api/v3/billing/subscription/cancel/${data.planSlug}`,
    data
  );

  return result.data;
};
export const checkoutPlanApi = () =>
  createRestApi<CheckoutPlan>('/api/v3/checkout/plan');
export const paymentIntentApi = () =>
  createRestApi<PaymentIntent>('/api/v3/billing/payment-intent');
export const checkoutApi = (planSlug: string) =>
  createRestApi<null, null, null, CheckoutPostResponse>(
    `/api/v3/checkout/${planSlug}`
  );

export const addOnsApi = () =>
  createRestApi<AddOn>(`/api/v3/teams/${team.id}/addons`);

export const addOnsPricesApi = () =>
  createRestApi<AddOnPrice>('/api/v3/addons/prices');

export const checkoutAddOnDetailsApi = (addOnType: AddOnType) =>
  axios.get<AddOnCheckoutDetails>(
    `/api/v3/teams/${team.id}/checkout/addon/${addOnType}`
  );

type PurchaseAddOnPayload = {
  addon: AddOnType;
  payment_method_id?: string;
};

export const purchaseAddOn = ({
  addon,
  payment_method_id,
}: PurchaseAddOnPayload) =>
  axios.post(`/api/v3/teams/${team.id}/addons`, { addon, payment_method_id });

export const cancelCustomDomainAddOn = (addOnUuid: string) =>
  axios.delete(`/api/v3/teams/${team.id}/addons/${addOnUuid}`);

export function useCreditCardsQuery(
  options?: UseQueryOptions<
    DefaultGetAllResponse<CreditCard>,
    unknown,
    DefaultGetAllResponse<CreditCard>,
    CreditCardsKeys['lists']
  >
) {
  return useQuery(
    creditCardsKeys.lists(),
    () => creditCardsApi().getAll(),
    options
  );
}
